import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { formatPercentage, useTranslation } from '@hotjar/translations';
import type { HeroStat } from '@hotjar/webapp/components/HeroStats/HeroStats';
import { HeroStats } from '@hotjar/webapp/components/HeroStats/HeroStats';
import type { i18n, TFunction } from 'i18next';
import type { Overview } from '@hotjar/feedback/domain/Surveys/Performance/Overview';
import { formatDuration } from './utils/dates';

const Lead = styled.div`
  max-width: 564px;
`;

const Heading = styled.h1`
  display: block;
  margin-bottom: ${tokens.spacer.x1};
  ${tokens.text('header5')}
`;

const Desc = styled.p`
  margin-bottom: ${tokens.spacer.x2};
  ${tokens.text('caption')}
`;

const HeroStatsWrapper = styled.div`
  margin: 0 0 ${tokens.spacer.x5};
`;

const BLUR_COMPLETION_RATE = 0.05;
const BLUR_IMPRESSIONS = 2000;
const BLUR_STARTS = 500;
const BLUR_COMPLETES = 10;
const BLUR_AVG_COMPLETION_TIME = 43;

const numberFormat = new Intl.NumberFormat('en-EN');

function formatPercent(percentage: number, i18n: i18n): string {
  return formatPercentage(percentage * 100, i18n);
}

function getCompletionRate(i18n: i18n, startedCount?: number, completedCount?: number): string {
  if (completedCount === undefined || startedCount === undefined) {
    return formatPercent(BLUR_COMPLETION_RATE, i18n);
  }
  if (startedCount === 0) {
    return formatPercent(0, i18n);
  }

  return formatPercent(completedCount / startedCount, i18n);
}

function getOverviewItems(
  overview: Overview | null,
  t: TFunction<'feedback', 'surveys.performance', 'feedback'>,
  i18n: i18n,
) {
  const overviewItems: HeroStat[] = [];

  if (overview?.impressions_count !== -1) {
    overviewItems.push({
      title: t('overview.impressions'),
      value: numberFormat.format(overview?.impressions_count ?? BLUR_IMPRESSIONS),
    });
  }

  if (overview?.started_responses_count !== -1) {
    overviewItems.push({
      title: t('overview.starts'),
      value: numberFormat.format(overview?.started_responses_count ?? BLUR_STARTS),
    });
  }

  if (overview?.completed_responses_count !== -1) {
    overviewItems.push({
      title: t('overview.completes'),
      value: numberFormat.format(overview?.completed_responses_count ?? BLUR_COMPLETES),
    });
  }

  if (overview?.started_responses_count !== -1 && overview?.completed_responses_count !== -1) {
    overviewItems.push({
      title: t('overview.completion-rate'),
      value: getCompletionRate(
        i18n,
        overview?.started_responses_count,
        overview?.completed_responses_count,
      ),
    });
  }

  if (overview?.average_completion_time_seconds !== -1) {
    overviewItems.push({
      title: t('overview.average-completion-time'),
      value: formatDuration(overview?.average_completion_time_seconds ?? BLUR_AVG_COMPLETION_TIME),
    });
  }

  return overviewItems;
}

type SurveyOverviewProps = {
  isRestricted: boolean;
  isLoading: boolean;
  overview: Overview | null;
};

export const SurveyOverview = ({
  isRestricted,
  isLoading,
  overview,
}: SurveyOverviewProps): JSX.Element | null => {
  const { t, i18n } = useTranslation('feedback', { keyPrefix: 'surveys.performance' });

  return (
    <>
      <Lead>
        <Heading>{t('overview.title')}</Heading>
        <Desc>{t('overview.description')}</Desc>
      </Lead>
      <HeroStatsWrapper>
        <HeroStats
          items={getOverviewItems(isRestricted ? null : overview, t, i18n)}
          loading={isLoading}
          blurred={isRestricted}
          contentMaxWidth={1000}
        />
      </HeroStatsWrapper>
    </>
  );
};
